import { safeStringifyCircular } from '@epihub/core/src/fn/stableStringify';
import { _isEmpty } from '@epihub/core/src/fn/object';
import { isNotEmptyString } from '@epihub/core/src/fn/string';
import { log } from '@epihub/core/src/_ctx/log';
import { type PojoError } from './types';
import type { TJSErrorParams } from './const';

export class JSError<E extends Record<string, any> = {}> extends Error {
  readonly __isJSError = true;

  readonly scope: string | undefined;
  // Store the message because we use super.message to store the stringified
  // version of this error, this way if we need to pass a pure JS "Error" we can
  // reconstruct the original JSError2 from error.message
  readonly _message: string;
  readonly parent: any;
  readonly shouldSkipLogs: boolean;
  readonly extras: E;

  constructor(params: TJSErrorParams<E>) {
    super();

    // Destructure without accessing params.extras (which doesn't exist)
    const {
      name,
      scope,
      message,
      parent,
      shouldSkipLogs = false,
      ...extras
    } = params;

    this.scope = scope;
    this._message = message;
    this.parent = parent;
    this.shouldSkipLogs = shouldSkipLogs;
    this.extras = extras as unknown as E;

    // Set the name property for proper Error behavior
    Object.defineProperty(this, 'name', {
      value: name,
      enumerable: false,
      configurable: true,
    });

    // Capture stack trace properly (V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, JSError);
    }

    // Set message after other properties are set so toString() works correctly
    Object.defineProperty(this, 'message', {
      // Do not use a getter because JSError properties are constant in any-case
      // Also a getter risks infinite recursion
      value: this.toString(),
      enumerable: false,
      configurable: true,
    });
  }

  toString() {
    return safeStringifyCircular({
      // Error props
      name: this.name,
      stack: this.stack,

      // Custom props
      __isJSError: this.__isJSError,
      scope: this.scope,
      message: this._message,
      parent: this.parent,
      shouldSkipLogs: this.shouldSkipLogs,
      ...this.extras,
    });
  }

  toJSON(): PojoError<JSError<E>> {
    return {
      // Error props
      name: this.name,
      stack: this.stack,

      // custom props
      __isJSError: this.__isJSError,
      scope: this.scope,
      message: this._message,
      parent: this.parent,
      shouldSkipLogs: this.shouldSkipLogs,
      ...this.extras,
    };
  }

  readonly log = () => {
    if (this.shouldSkipLogs) {
      return;
    }

    const message = [
      this._message,
      this.stack,
    ]
      .filter(isNotEmptyString)
      .join('\n');

    log.e(this.scope || this.name, message, {
      ..._isEmpty(this.stack) ? { stack: this.stack } : {},
      ..._isEmpty(this.parent) ? { parent: this.parent } : {},
    });
  };
}
